import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCallback } from 'react';

interface ProfileData {
  id: string;
  username: string;
  name: string;
  bio?: string;
  image?: string;
  image_thumb?: string;
  created_at: string;
  archetype?: string;
}

/**
 * Hook that provides prefetching functions for profile and user data.
 * Call prefetchProfile on mouseEnter/focus to preload data before navigation.
 */
export function useProfilePrefetch() {
  const queryClient = useQueryClient();

  const prefetchProfile = useCallback(async (username: string) => {
    // Don't refetch if data is already fresh (less than 30 seconds old)
    const existingData = queryClient.getQueryData(['profile', username]);
    if (existingData) return;

    // Prefetch profile data
    queryClient.prefetchQuery({
      queryKey: ['profile', username],
      queryFn: async () => {
        const { data, error } = await supabase
          .rpc('get_public_profile_safe', { profile_username: username });
        
        if (error) throw error;
        return data?.[0] as ProfileData | null;
      },
      staleTime: 1000 * 60 * 5, // 5 minutes
    });

    // Also prefetch the user's books in parallel
    queryClient.prefetchQuery({
      queryKey: ['profile-books', username],
      queryFn: async () => {
        // First get the profile ID
        const { data: profileData } = await supabase
          .rpc('get_public_profile_safe', { profile_username: username });
        
        if (!profileData?.[0]?.id) return [];

        const profileId = profileData[0].id;

        // Fetch user's books
        const { data: booksData } = await supabase
          .from('user_books')
          .select(`
            id,
            rating_star,
            shelf,
            review_text,
            read_at,
            started_at,
            finished_at,
            paused_at,
            created_at,
            book_id,
            current_page,
            total_pages,
            reading_progress_percent,
            last_reading_date,
            is_purchased
          `)
          .eq('user_id', profileId)
          .eq('privacy', 'public')
          .order('created_at', { ascending: false })
          .limit(50);

        if (!booksData?.length) return [];

        // Get book details
        const bookIds = booksData.map(item => item.book_id).filter(Boolean);
        const { data: bookDetails } = await supabase
          .from('books')
          .select('id, title, authors, cover_url, cover_cache_url, isbn13, isbn10, page_count')
          .in('id', bookIds);

        // Merge data
        return booksData.map(userBook => {
          const bookDetail = bookDetails?.find(book => book.id === userBook.book_id);
          if (!bookDetail) return null;
          return { ...bookDetail, ...userBook };
        }).filter(Boolean);
      },
      staleTime: 1000 * 60 * 5, // 5 minutes
    });
  }, [queryClient]);

  const prefetchUserProfile = useCallback(async (userId: string) => {
    // Don't refetch if data is already fresh
    const existingData = queryClient.getQueryData(['user-profile', userId]);
    if (existingData) return;

    queryClient.prefetchQuery({
      queryKey: ['user-profile', userId],
      queryFn: async () => {
        const { data, error } = await supabase
          .from('profiles')
          .select('id, username, name, image, image_thumb, archetype')
          .eq('id', userId)
          .maybeSingle();
        
        if (error) throw error;
        return data;
      },
      staleTime: 1000 * 60 * 5,
    });
  }, [queryClient]);

  return { prefetchProfile, prefetchUserProfile };
}

/**
 * Standalone function to prefetch own profile data.
 * Called after auth to warm the cache.
 */
export async function prefetchOwnProfile(userId: string, username: string): Promise<void> {
  try {
    // Pre-warm common profile queries
    const [profileResult, booksResult, followsResult] = await Promise.all([
      supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle(),
      supabase
        .from('user_books')
        .select('id, shelf, rating_star, book_id')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(20),
      Promise.all([
        supabase.from('follows').select('id').eq('following_id', userId),
        supabase.from('follows').select('id').eq('follower_id', userId),
      ]),
    ]);

    console.log('[Profile Prefetch] Warmed cache for', username);
  } catch (error) {
    console.error('[Profile Prefetch] Error:', error);
  }
}
